<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-10">
        <div class="flex flex-col w-full">
          <form-card
            :title="t('general')"
            :description="t(tKey + '.general.description')"
          >
            <div class="flex flex-col gap-y-5">
              <!--              <FormInput-->
              <!--                v-if="rawVisibilityTransfer.responsibleUser"-->
              <!--                v-model="rawVisibilityTransfer.responsibleUser.fullName"-->
              <!--                disabled-->
              <!--                class="col-start-1 col-end-2"-->
              <!--                type="text"-->
              <!--                :label="t('visibility.transfer.responsibleUser')"-->
              <!--              />-->
              <Multiselect
                v-model="rawVisibilityTransfer.responsibleUser"
                :error="errors?.responsibleUserForTransfer"
                :options="userList"
                :label="t('visibility.planning.responsibleUserForTransfer')"
                :loading="loadingUsers"
                :disabled="loadingUsers"
                class="col-start-1 col-end-2"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
              />
              <FormInput
                v-model="rawVisibilityTransfer.visibilityBicycle.number"
                disabled
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.bicycle')"
              />
              <FormInput
                v-model="rawVisibilityTransfer.visibilityPlanning.campaign.name"
                disabled
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.campaign')"
              />
              <FormInput
                v-if="rawVisibilityTransfer.oldLocation"
                v-model="rawVisibilityTransfer.oldLocation.name"
                disabled
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.oldLocation')"
              />
              <FormInput
                v-model="rawVisibilityTransfer.newLocation.name"
                disabled
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.newLocation')"
              />
              <DatePicker
                v-model="rawVisibilityTransfer.transferTime"
                disabled
                :error="errors?.transferTime"
                mode="datetime"
                class="w-full"
                :label="t('visibility.transfer.transferTime')"
              />
              <DatePicker
                v-model="rawVisibilityTransfer.actualTransferTime"
                :disabled="rawVisibilityTransfer.done"
                :error="errors?.actualTransferTime"
                mode="datetime"
                class="w-full"
                :label="t('visibility.transfer.actualTransferTime')"
              />
              <FormInput
                v-if="rawVisibilityTransfer.done && rawVisibilityTransfer.confirmedByUser"
                v-model="rawVisibilityTransfer.confirmedByUser.fullName"
                disabled
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.transfer.confirmedByUser')"
              />
              <DatePicker
                v-if="rawVisibilityTransfer.done && rawVisibilityTransfer.confirmationTime"
                v-model="rawVisibilityTransfer.confirmationTime"
                disabled
                mode="datetime"
                class="w-full"
                :label="t('visibility.transfer.confirmationTime')"
              />
              <FormInput
                v-model="rawVisibilityTransfer.newDesign"
                :error="errors?.newDesign"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.newDesign')"
                :placeholder="t('visibility.newDesign')"
              />
              <FormInput
                v-model="rawVisibilityTransfer.comment"
                :error="errors?.comment"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('visibility.comment')"
                :placeholder="t('visibility.comment')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import DatePicker from '@utils/inputs/DatePicker.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import { collOptionsProvider, useProviderOpt } from '@utils/table/hooks/provider';

import Multiselect from '@utils/Multiselect.vue';
import type {
  Region, User,
  VisibilityTransfer,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  visibilityTransfer?: VisibilityTransfer;
}

const emit = defineEmits(['update:open', 'update:visibilityTransfer', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityTransfer = useVModel(props, 'visibilityTransfer', emit);

const isNew = computed(() => !props.visibilityTransfer);
const tKey = computed(() =>
  (isNew.value ? 'pages.visibility.transfer.modal.new' : 'pages.visibility.transfer.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (visibilityTransfer?: VisibilityTransfer) => (visibilityTransfer
  ? {
    ...visibilityTransfer,
    responsibleUser: visibilityTransfer?.responsibleUser?.['@id'],
  }
  : {
    id: undefined,
    campaign: undefined,
    region: undefined,
    visibilityBicycle: undefined,
    transferTime: undefined,
    actualTransferTime: undefined,
    newLocation: undefined,
    responsibleUser: undefined,
    newDesign: '',
    comment: '',
    done: false,
  });

const {
  data: userList,
  loading: loadingUsers,
} = useProviderOpt<User>(
  '/users',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    filter: {
      noDrivers: 'true',
      roles: 'ROLE_VISIBILITY',
    },
  },
  (value: User) => ({
    label: value.fullName,
    value: value['@id'],
  }),
  {
    immediate: true,
  },
);

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);
const regions = ref([]);

onMounted(async () => {
  regions.value = await fetchRegions();
});

const rawVisibilityTransfer = syncRef(internalVisibilityTransfer, (visibilityTransfer: VisibilityTransfer) => ({
  ...initialData(visibilityTransfer),
  region: visibilityTransfer?.region?.['@id'],
}));

const {
  data: visibilityTransfer,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    actualTransferTime: date()
      .nullable().required(t('validation.notEmpty')),
    responsibleUser: string()
      .nullable(),

    newDesign: string()
      .nullable()
      .required(t('validation.notEmpty')),
    comment: string()
      .nullable(),
  }),
  rawVisibilityTransfer,
);

function resetData() {
  rawVisibilityTransfer.value = { ...initialData() } as unknown as typeof visibilityTransfer['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityTransfer.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.visibilityTransfer;
    const url = edit ? props.visibilityTransfer['@id'] : '/visibility_transfers';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        actualTransferTime: visibilityTransfer.value.actualTransferTime,
        responsibleUser: visibilityTransfer.value.responsibleUser,
        completionPicture: visibilityTransfer.value.completionPicture,
        newDesign: visibilityTransfer.value.newDesign,
        comment: visibilityTransfer.value.comment,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
